/**
 * Custom Analytics Module
 * Tracks sessions, events, and conversions to Supabase
 */

import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/config.js';
import { getDistinctId, getOriginalAnonId } from './posthog.js';
import { getGAClientId } from './ga4.js';
import { getUTMData, getDeviceInfo, getReferrer, getSessionId, isDevelopment, waitForValue } from './utils.js';
import { authService } from '../../auth/auth-service.js';
import { entitlementService } from '../../entitlements/entitlement-service.js';

const SESSION_TRACKED_KEY = 'visualizer_session_tracked';

// Cache for current session ID in Supabase (UUID, not local session ID)
let currentSupabaseSessionId = null;

// Queue for events that arrive before init completes (auth not ready yet)
let initialized = false;
let pendingEvents = [];

/**
 * Insert data to Supabase table (fire-and-forget)
 * @param {string} table - Table name
 * @param {Object} data - Data to insert
 * @returns {Promise<Object|null>} - Inserted row or null on failure
 */
async function insertToSupabase(table, data) {
  try {
    // Use authenticated user's token if available, otherwise anon key
    const accessToken = authService.getAccessToken() || SUPABASE_ANON_KEY;

    const response = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      console.warn(`[CustomAnalytics] Failed to insert to ${table}:`, response.status);
      return null;
    }

    const result = await response.json();
    if (isDevelopment()) console.log(`[CustomAnalytics] Tracked ${table}`);
    return Array.isArray(result) ? result[0] : result;
  } catch (error) {
    // Fire-and-forget - log warning but don't throw
    console.warn(`[CustomAnalytics] Error tracking ${table}:`, error.message);
    return null;
  }
}

/**
 * Get current user context
 * @returns {Promise<Object>}
 */
async function getUserContext() {
  const isAuthenticated = authService.isAuthenticated();
  const isAnonymous = authService.isAnonymous();
  const userId = isAuthenticated ? authService.getUser()?.id : null;

  let tier = 'free';
  try {
    tier = await entitlementService.getTier();
  } catch (e) {
    // Ignore errors, use default
  }

  return {
    user_id: userId,
    is_anonymous: isAnonymous || !isAuthenticated,
    tier
  };
}

/**
 * Track a session (page visit)
 * Only tracks once per browser session using sessionStorage
 * @returns {Promise<string|null>} - Session ID or null
 */
export async function trackSession() {
  // Check if already tracked this session
  if (sessionStorage.getItem(SESSION_TRACKED_KEY)) {
    if (isDevelopment()) console.log('[CustomAnalytics] Session already tracked');
    return currentSupabaseSessionId;
  }

  // Use original anonymous ID for consistent tracking across auth state changes
  const posthogId = getOriginalAnonId() || getDistinctId();
  const gaClientId = getGAClientId();

  // Skip if no distinct ID available
  if (!posthogId) {
    console.warn('[CustomAnalytics] No PostHog ID, delaying session tracking');
    return null;
  }

  const utmData = getUTMData();
  const deviceInfo = getDeviceInfo();
  const userContext = await getUserContext();

  const sessionData = {
    // User identification
    user_id: userContext.user_id,
    posthog_distinct_id: posthogId,
    ga_client_id: gaClientId,

    // Attribution
    ...utmData,
    referrer: getReferrer(),

    // Device information
    ...deviceInfo,

    // Context
    landing_page: window.location.pathname,

    // User state
    is_anonymous: userContext.is_anonymous,
    tier: userContext.tier
  };

  // Insert and capture session ID
  const result = await insertToSupabase('visualizer_sessions', sessionData);
  if (result?.id) {
    currentSupabaseSessionId = result.id;
    sessionStorage.setItem('visualizer_supabase_session_id', result.id);
    // Only mark as tracked AFTER successful insert
    sessionStorage.setItem(SESSION_TRACKED_KEY, 'true');
  }

  return currentSupabaseSessionId;
}

/**
 * Get the current Supabase session ID
 * @returns {string|null}
 */
export function getSupabaseSessionId() {
  if (currentSupabaseSessionId) return currentSupabaseSessionId;
  return sessionStorage.getItem('visualizer_supabase_session_id') || null;
}

/**
 * Track an event
 * @param {string} eventName - Event name
 * @param {string} eventCategory - Event category
 * @param {Object} properties - Event properties
 * @param {Object} context - Additional context
 */
export async function trackEvent(eventName, eventCategory, properties = {}, context = {}) {
  // Queue events that arrive before init (auth not ready → Supabase will 401)
  if (!initialized) {
    pendingEvents.push({ eventName, eventCategory, properties, context });
    return;
  }

  // Use original anonymous ID for consistent tracking
  const posthogId = getOriginalAnonId() || getDistinctId();
  const userContext = await getUserContext();

  const eventData = {
    // Session and user linkage
    session_id: getSupabaseSessionId(),
    user_id: userContext.user_id,
    posthog_distinct_id: posthogId,

    // Event data
    event_name: eventName,
    event_category: eventCategory,
    properties: properties,

    // Context at time of event
    visualizer_type: context.visualizerType || null,
    track_count: context.trackCount || null,
    tier: userContext.tier,
    is_anonymous: userContext.is_anonymous
  };

  // Fire-and-forget insert
  await insertToSupabase('visualizer_events', eventData);
}

/**
 * Track a conversion event
 * @param {string} conversionType - Type of conversion
 * @param {Object} details - Conversion details
 */
export async function trackConversion(conversionType, details = {}) {
  // Use original anonymous ID for consistent tracking
  const posthogId = getOriginalAnonId() || getDistinctId();
  const userContext = await getUserContext();
  const utmData = getUTMData();

  const conversionData = {
    // User identification
    user_id: userContext.user_id,
    posthog_distinct_id: posthogId,

    // Conversion type
    conversion_type: conversionType,

    // Attribution
    utm_source: utmData.utm_source || null,
    utm_medium: utmData.utm_medium || null,
    utm_campaign: utmData.utm_campaign || null,
    referrer: getReferrer(),

    // Conversion details
    from_tier: details.fromTier || null,
    to_tier: details.toTier || null,
    revenue_cents: details.revenueCents || null,
    trigger_location: details.triggerLocation || null,

    // Additional metadata
    properties: details.properties || {}
  };

  // Fire-and-forget insert
  await insertToSupabase('visualizer_conversions', conversionData);
}

/**
 * Update user profile with analytics IDs
 * Called after authentication to link PostHog/GA IDs to the user profile
 */
export async function linkAnalyticsToUser() {
  if (!authService.isAuthenticated()) return;

  const userId = authService.getUser()?.id;
  if (!userId) return;

  // Use original anonymous ID for profile linking
  const posthogId = getOriginalAnonId() || getDistinctId();
  const gaClientId = getGAClientId();
  const utmData = getUTMData();

  // Only update if we have at least one ID to set
  if (!posthogId && !gaClientId) return;

  const updateData = {};

  if (posthogId) {
    updateData.posthog_distinct_id = posthogId;
  }
  if (gaClientId) {
    updateData.ga_client_id = gaClientId;
  }

  // Set initial UTM if we have it and it's the first session
  if (utmData.utm_source && !sessionStorage.getItem('visualizer_utm_linked')) {
    updateData.initial_utm_source = utmData.utm_source;
    updateData.initial_utm_medium = utmData.utm_medium || null;
    updateData.initial_utm_campaign = utmData.utm_campaign || null;
    sessionStorage.setItem('visualizer_utm_linked', 'true');
  }

  if (Object.keys(updateData).length === 0) return;

  try {
    const accessToken = authService.getAccessToken();
    const response = await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify(updateData)
    });

    if (response.ok) {
      if (isDevelopment()) console.log('[CustomAnalytics] Linked analytics IDs to user profile');
    }
  } catch (error) {
    console.warn('[CustomAnalytics] Failed to link analytics to user:', error.message);
  }
}

/**
 * Initialize custom analytics
 * Polls for PostHog distinct ID readiness, then tracks session
 */
export async function init() {
  // Poll for PostHog distinct ID instead of a fixed delay
  const posthogId = await waitForValue(() => getDistinctId(), { maxAttempts: 15, intervalMs: 200 });
  if (!posthogId) {
    console.warn('[CustomAnalytics] PostHog ID not available after polling, proceeding anyway');
  }

  await trackSession();

  // Mark as initialized and flush any events that were queued before auth was ready
  initialized = true;
  const queued = pendingEvents;
  pendingEvents = [];
  if (queued.length > 0) {
    if (isDevelopment()) console.log(`[CustomAnalytics] Flushing ${queued.length} queued events`);
    for (const event of queued) {
      trackEvent(event.eventName, event.eventCategory, event.properties, event.context).catch(() => {});
    }
  }

  if (isDevelopment()) console.log('[CustomAnalytics] Initialized');
}
